import { notification } from "antd";
import classNames from "classnames";
import { get } from "lodash";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { BiChevronLeft } from "react-icons/bi";
import { useLocation, useNavigate } from "react-router-dom";
import Header from "shared/components/Header";
import { useMutation } from "shared/hooks/useApi";
import { NAME_SPACES } from "shared/locales/constants";
import {
  IMPORT_PRICERS_FROM_FILE,
  VALIDATE_PRICER_IMPORT,
} from "utils/api/graphql/mutations/pricers";
import { PRICERS } from "utils/api/graphql/queries/pricers";
import { INITIAL_VALUES, SCREENS } from "utils/constants";
import {
  DEFAULT_RISK,
  GEO_FORMATS,
  IMPORT_STEPS,
  IMPORT_STRATEGIES,
} from "./constants";
import StepAnalysis from "./StepAnalysis";
import StepGeoZone from "./StepGeoZone";
import StepImporting from "./StepImporting";
import StepMapping from "./StepMapping";
import StepResult from "./StepResult";
import StepUpload from "./StepUpload";
import StepValidation from "./StepValidation";
import StepValueMapping from "./StepValueMapping";

const STEPS = [
  IMPORT_STEPS.UPLOAD,
  IMPORT_STEPS.MAPPING,
  IMPORT_STEPS.VALIDATION,
  IMPORT_STEPS.VALUE_MAPPING,
  IMPORT_STEPS.GEO_ZONE,
  IMPORT_STEPS.ANALYSIS,
  IMPORT_STEPS.IMPORTING,
  IMPORT_STEPS.RESULT,
];

const ImportWizard = ({ onBack }) => {
  const { t } = useTranslation(NAME_SPACES.PRIVATE.ADMIN.PRICER);
  const IMPORT = t("IMPORT", { returnObjects: true });
  const { state: locationState } = useLocation();
  const navigate = useNavigate();

  const [currentStep, setCurrentStep] = useState(0);
  const [fileData, setFileData] = useState(null);
  const [validationResult, setValidationResult] = useState(null);
  const [mapping, setMapping] = useState({});
  const [format, setFormat] = useState(null);
  const [strategy, setStrategy] = useState(IMPORT_STRATEGIES.ALL);
  const [importResult, setImportResult] = useState(null);
  const [regimeMapping, setRegimeMapping] = useState(null);
  const [beneficiaryTypeMapping, setBeneficiaryTypeMapping] = useState(null);
  const [zoneDepartmentMapping, setZoneDepartmentMapping] = useState(null);

  const [valueMappingState, setValueMappingState] = useState({
    regimeMap: {},
    benefTypeMap: {},
    saveRegime: false,
    saveBenef: false,
    regimeRefName: "",
    benefRefName: "",
    selectedRegimeRefId: null,
    selectedBenefRefId: null,
  });

  const [geoZoneState, setGeoZoneState] = useState({
    zoneDeptMap: {},
    saveAsRef: true,
    refName: "",
    selectedRefId: null,
  });

  const [analysisConfirmed, setAnalysisConfirmed] = useState(false);
  const [priceInCents, setPriceInCents] = useState(false);

  const [validateImport, { loading: validating }] = useMutation(
    VALIDATE_PRICER_IMPORT,
  );

  const [executeImport] = useMutation(IMPORT_PRICERS_FROM_FILE, {
    refetchQueries: [
      {
        query: PRICERS,
        awaitRefetchQueries: true,
        variables: INITIAL_VALUES,
      },
    ],
  });

  const stepName = STEPS[currentStep];
  const isZonier = format === GEO_FORMATS.ZONIER;

  const getVisibleSteps = () => {
    if (isZonier) return STEPS;
    return STEPS.filter((s) => s !== IMPORT_STEPS.GEO_ZONE);
  };

  const visibleSteps = getVisibleSteps();
  const visibleStepIndex = visibleSteps.indexOf(stepName);

  const handleFileUpload = async (base64, filename) => {
    setFileData({ base64, filename });
    try {
      const { data } = await validateImport({
        variables: {
          data: { base64, filename },
        },
      });
      const result = get(data, "validatePricerImport");
      setValidationResult(result);
      setMapping(result.autoMapping || result.mapping || {});
      setFormat(result.format);
      setCurrentStep(STEPS.indexOf(IMPORT_STEPS.MAPPING));
    } catch (error) {
      notification.error({
        message: IMPORT.ERRORS.UPLOAD_FAILED,
        description: error.message,
      });
    }
  };

  const handleMappingConfirm = async (updatedMapping) => {
    setMapping(updatedMapping);
    try {
      const { data } = await validateImport({
        variables: {
          data: {
            base64: fileData.base64,
            filename: fileData.filename,
            mapping: updatedMapping,
            format,
          },
        },
      });
      const result = get(data, "validatePricerImport");
      setValidationResult(result);
      setCurrentStep(STEPS.indexOf(IMPORT_STEPS.VALIDATION));
    } catch (error) {
      notification.error({
        message: IMPORT.ERRORS.VALIDATION_FAILED,
        description: error.message,
      });
    }
  };

  const handleValidationNext = () => {
    setCurrentStep(STEPS.indexOf(IMPORT_STEPS.VALUE_MAPPING));
  };

  const handleValueMappingConfirm = (regMap, benefMap) => {
    setRegimeMapping(regMap);
    setBeneficiaryTypeMapping(benefMap);
    if (isZonier) {
      setCurrentStep(STEPS.indexOf(IMPORT_STEPS.GEO_ZONE));
    } else {
      setCurrentStep(STEPS.indexOf(IMPORT_STEPS.ANALYSIS));
    }
  };

  const handleGeoZoneConfirm = (zoneDeptMap) => {
    setZoneDepartmentMapping(zoneDeptMap);
    setCurrentStep(STEPS.indexOf(IMPORT_STEPS.ANALYSIS));
  };

  const handleAnalysisNext = () => {
    handleExecuteImport();
  };

  const handleExecuteImport = async () => {
    setCurrentStep(STEPS.indexOf(IMPORT_STEPS.IMPORTING));
    try {
      const { data } = await executeImport({
        variables: {
          data: {
            base64: fileData.base64,
            filename: fileData.filename,
            mapping,
            format,
            strategy,
            risk: locationState?.risk || DEFAULT_RISK,
            processes: [],
            regimeMapping,
            beneficiaryTypeMapping,
            zoneDepartmentMapping,
            priceInCents,
          },
        },
      });
      const result = get(data, "importPricersFromFile");
      if (result) {
        setImportResult(result);
      } else {
        setImportResult({
          success: false,
          errors: [{ key: "UNEXPECTED_ERROR", params: {} }],
        });
      }
      setCurrentStep(STEPS.indexOf(IMPORT_STEPS.RESULT));
    } catch (error) {
      const gqlMsg = get(error, "graphQLErrors[0].message", "");
      const msg = gqlMsg || error.message || "";
      let parsedError;
      try {
        parsedError = JSON.parse(msg);
      } catch (_e) {
        parsedError = null;
      }
      const errorObj = parsedError?.key
        ? parsedError
        : { key: "UNEXPECTED_ERROR", params: { message: msg } };
      setImportResult({
        success: false,
        errors: [errorObj],
      });
      setCurrentStep(STEPS.indexOf(IMPORT_STEPS.RESULT));
    }
  };

  const handleFinish = () => {
    navigate(SCREENS.PRIVATE.ADMIN.PRICERS.LIST.path);
  };

  const handleReset = () => {
    setCurrentStep(0);
    setFileData(null);
    setValidationResult(null);
    setMapping({});
    setFormat(null);
    setStrategy(IMPORT_STRATEGIES.ALL);
    setImportResult(null);
    setRegimeMapping(null);
    setBeneficiaryTypeMapping(null);
    setZoneDepartmentMapping(null);
    setValueMappingState({
      regimeMap: {},
      benefTypeMap: {},
      saveRegime: false,
      saveBenef: false,
      regimeRefName: "",
      benefRefName: "",
      selectedRegimeRefId: null,
      selectedBenefRefId: null,
    });
    setGeoZoneState({
      zoneDeptMap: {},
      saveAsRef: true,
      refName: "",
      selectedRefId: null,
    });
    setAnalysisConfirmed(false);
    setPriceInCents(false);
  };

  const goBack = () => {
    if (currentStep === 0) {
      onBack();
      return;
    }
    const currentVisible = visibleSteps.indexOf(stepName);
    if (currentVisible > 0) {
      const prevStep = visibleSteps[currentVisible - 1];
      setCurrentStep(STEPS.indexOf(prevStep));
    }
  };

  const stepLabels = visibleSteps.map((step) => {
    const labelMap = {
      [IMPORT_STEPS.UPLOAD]: IMPORT.STEPS.UPLOAD,
      [IMPORT_STEPS.MAPPING]: IMPORT.STEPS.MAPPING,
      [IMPORT_STEPS.VALIDATION]: IMPORT.STEPS.VALIDATION,
      [IMPORT_STEPS.VALUE_MAPPING]: IMPORT.STEPS.VALUE_MAPPING,
      [IMPORT_STEPS.GEO_ZONE]: IMPORT.STEPS.GEO_ZONE,
      [IMPORT_STEPS.ANALYSIS]: IMPORT.STEPS.ANALYSIS,
      [IMPORT_STEPS.IMPORTING]: IMPORT.STEPS.IMPORTING,
      [IMPORT_STEPS.RESULT]: IMPORT.STEPS.RESULT,
    };
    return labelMap[step] || step;
  });

  return (
    <>
      <Header
        title={IMPORT.TITLE}
        subtitle={IMPORT.SUBTITLE}
        actions={
          stepName === IMPORT_STEPS.UPLOAD
            ? [
                {
                  text: IMPORT.BACK,
                  onClick: goBack,
                  type: "primary--outlined",
                  icon: <BiChevronLeft size={20} />,
                },
              ]
            : []
        }
      />
      <div className="pricers-import__stepper">
        {stepLabels.map((label, idx) => {
          const isActive = idx === visibleStepIndex;
          const isDone = idx < visibleStepIndex;
          return (
            <div key={idx} className="pricers-import__step-item">
              <div className="pricers-import__step-row">
                <div
                  className={classNames("pricers-import__step-connector", {
                    "pricers-import__step-connector--done": isDone || isActive,
                    "pricers-import__step-connector--hidden": idx === 0,
                  })}
                />
                <span
                  className={classNames("pricers-import__step-number", {
                    "pricers-import__step-number--active": isActive,
                    "pricers-import__step-number--done": isDone,
                  })}
                >
                  {isDone ? "\u2713" : idx + 1}
                </span>
                <div
                  className={classNames("pricers-import__step-connector", {
                    "pricers-import__step-connector--done": isDone,
                    "pricers-import__step-connector--hidden":
                      idx === stepLabels.length - 1,
                  })}
                />
              </div>
              <span
                className={classNames("pricers-import__step-label", {
                  "pricers-import__step-label--active": isActive,
                  "pricers-import__step-label--done": isDone,
                })}
              >
                {label}
              </span>
            </div>
          );
        })}
      </div>

      {stepName === IMPORT_STEPS.UPLOAD && (
        <StepUpload onUpload={handleFileUpload} loading={validating} />
      )}
      {stepName === IMPORT_STEPS.MAPPING && validationResult && (
        <StepMapping
          fields={validationResult.fields}
          headers={validationResult.headers}
          autoMapping={validationResult.autoMapping}
          mapping={mapping}
          columnSamples={validationResult.columnSamples}
          format={format}
          onConfirm={handleMappingConfirm}
          onMappingChange={setMapping}
          loading={validating}
          priceInCents={priceInCents}
          onPriceInCentsChange={setPriceInCents}
          onBack={goBack}
          backLabel={IMPORT.BACK_CHANGE_FILE}
        />
      )}
      {stepName === IMPORT_STEPS.VALIDATION && validationResult && (
        <StepValidation
          validationResult={validationResult}
          onNext={handleValidationNext}
          onBack={goBack}
          backLabel={IMPORT.BACK_EDIT_MAPPING}
        />
      )}
      {stepName === IMPORT_STEPS.VALUE_MAPPING && validationResult && (
        <StepValueMapping
          preview={validationResult.preview}
          onConfirm={handleValueMappingConfirm}
          persistedState={valueMappingState}
          onStateChange={setValueMappingState}
          onBack={goBack}
          backLabel={IMPORT.BACK_REVIEW_VALIDATION}
        />
      )}
      {stepName === IMPORT_STEPS.GEO_ZONE && validationResult && (
        <StepGeoZone
          fileZones={get(validationResult, "preview.zones", []).filter(Boolean)}
          onConfirm={handleGeoZoneConfirm}
          persistedState={geoZoneState}
          onStateChange={setGeoZoneState}
          onBack={goBack}
          backLabel={IMPORT.BACK_REVIEW_VALUES}
        />
      )}
      {stepName === IMPORT_STEPS.ANALYSIS && validationResult && (
        <StepAnalysis
          preview={validationResult.preview}
          format={format}
          strategy={strategy}
          onStrategyChange={setStrategy}
          onNext={handleAnalysisNext}
          confirmed={analysisConfirmed}
          onConfirmedChange={setAnalysisConfirmed}
          onBack={goBack}
          backLabel={
            isZonier ? IMPORT.BACK_REVIEW_ZONES : IMPORT.BACK_REVIEW_VALUES
          }
        />
      )}
      {stepName === IMPORT_STEPS.IMPORTING && <StepImporting />}
      {stepName === IMPORT_STEPS.RESULT && (
        <StepResult
          result={
            importResult || {
              success: false,
              errors: [
                {
                  key: "UNEXPECTED_ERROR",
                  params: { message: "" },
                },
              ],
            }
          }
          onFinish={handleFinish}
          onReset={handleReset}
        />
      )}
    </>
  );
};

export default ImportWizard;
